import { DragAndDropHandler } from '@/shared/services/drop-file/drag-and-drop-handler'
import { FunctionQueue } from '@/commons/function-queue'
import { loggerFor } from '@/shared/utils/logger'

export interface DragAndDrop {
  /**
   * Add a new handler that get's notified immediately when the user dropped a file onto the page
   */
  addHandler(handler: DragAndDropHandler): void

  /**
   * Call all registered dragenter handlers in fifo order
   */
  handleDragEnter(event: DragEvent): void

  /**
   * Call all registered dragleave handlers in fifo order
   */
  handleDragLeave(event: DragEvent): void

  /**
   * Iterate over all files that were dropped and call the registered handlers in fifo order
   */
  handleDrop(event: DragEvent): void

  /**
   * Remove the given handler
   */
  removeHandler(handler: DragAndDropHandler): void

  /**
   * Initialize the drag and drop handlers
   */
  start(): void
}

export class DragAndDropService implements DragAndDrop {
  private dragLeaveHandlers: FunctionQueue<DragEvent> = new FunctionQueue<DragEvent>()
  private dragEnterHandlers: FunctionQueue<DragEvent> = new FunctionQueue<DragEvent>()
  private dropHandlers: FunctionQueue<File> = new FunctionQueue<File>()

  private log = loggerFor('DragAndDropService')

  public addHandler(handler: DragAndDropHandler): void {
    if (handler.dragenter != null) {
      this.log.debug('Add dragenter handler')

      this.dragEnterHandlers.add(handler.dragenter)
    }

    if (handler.dragleave != null) {
      this.log.debug('Add dragleave handler')

      this.dragLeaveHandlers.add(handler.dragleave)
    }

    if (handler.drop != null) {
      this.log.debug('Add drop handler')

      this.dropHandlers.add(handler.drop)
    }
  }

  public end(): void {
    document.body.removeEventListener('dragenter', this.dragEnterListener)
  }

  public handleDragEnter(event: DragEvent): void {
    if (this.containsFiles(event)) {
      this.emitDragEnter(event)
    }
  }

  public handleDragLeave(event: DragEvent): void {
    if (this.containsFiles(event)) {
      this.emitDragLeave(event)
    }
  }

  public handleDrop(event: DragEvent): FileList | undefined {
    let droppedFiles: FileList | undefined

    if (this.containsFiles(event)) {
      droppedFiles = event.dataTransfer?.files

      if (droppedFiles && droppedFiles.length > 0) {
        for (const file of droppedFiles) {
          this.emitDrop(file)
        }
      }
    }

    return droppedFiles
  }

  public removeHandler(handler: DragAndDropHandler): void {
    if (handler.dragenter != null) {
      this.log.debug('Remove dragenter handler')

      this.dragEnterHandlers.remove(handler.dragenter)
    }

    if (handler.dragleave != null) {
      this.log.debug('Remove dragleave handler')

      this.dragLeaveHandlers.remove(handler.dragleave)
    }

    if (handler.drop != null) {
      this.log.debug('Remove drop handler')

      this.dropHandlers.remove(handler.drop)
    }
  }

  public start(): void {
    document.body.addEventListener('dragenter', this.dragEnterListener)
  }

  /**
   * Check if the drag event contains files and if so then prevent the default action
   * @param event
   */
  private containsFiles(event: DragEvent): boolean {
    const containsFiles = !!event.dataTransfer?.types?.reduce((a, b) => a || b === 'Files', false)

    if (containsFiles) {
      event.preventDefault()
    }

    return containsFiles
  }

  private dragEnterListener = (event: DragEvent) => this.handleDragEnter(event)

  private emitDragEnter(event: DragEvent): void {
    this.dragEnterHandlers.call(event)
  }

  private emitDragLeave(event: DragEvent): void {
    this.dragLeaveHandlers.call(event)
  }

  private emitDrop(file: File): void {
    this.dropHandlers.call(file)
  }
}
